/** @format */
import React, {useContext, useState} from 'react'

import FormspreeSelect from '../../components/tailwind/FormspreeSelect'
import LoaderButton from '../../components/tailwind/LoaderButton'
import Modal from '../../components/tailwind/Modal'
import {
  ContextModal,
  ModalContext,
} from '../../components/tailwind/ModalContext'
import {AccountContext} from '../../contexts/AccountContext'
import {LoadingContext} from '../../contexts/LoadingContext'

// Above this many options, filtering beats scanning.
const SEARCHABLE_ABOVE = 5

/**
 * The endpoint only accepts an address that's already a verified linked email,
 * so this picks from those — or, with none to pick, says how to get one.
 */
export default function ChangeEmailModal({modal}) {
  const {ready} = useContext(LoadingContext)
  const {emails, profile, patchAccount, reloadAccount} =
    useContext(AccountContext)
  const {closeCurrentModal} = useContext(ModalContext)
  const [newEmail, setNewEmail] = useState()

  const selectable = emails.verified.filter(e => e !== profile.email)
  const waiting = emails.pending.filter(e => e !== profile.email)

  // The current address leads the list, disabled, so the closed dropdown shows
  // it instead of "Select...".
  const options = [
    {
      label: `${profile.email} — current`,
      value: profile.email,
      isDisabled: true,
    },
    ...selectable.map(e => ({label: e, value: e})),
    ...waiting.map(e => ({
      label: `${e} — waiting on verification`,
      value: e,
      isDisabled: true,
    })),
  ]

  return (
    <ContextModal
      modal={modal}
      title={'Select Account Email'}
      onClose={() => {
        setNewEmail()
      }}
    >
      {selectable.length > 0 ? (
        <>
          <p className="text-sm text-gray-500">
            Select which of your <strong>Linked Emails</strong> to use as your
            account email.
          </p>
          <p className="mb-4 mt-4 text-sm text-gray-500">
            Or{' '}
            <a
              className="primaryLink cursor-pointer"
              onClick={() => {
                closeCurrentModal()
                showLinkedEmails()
              }}
            >
              add a new <strong>Linked Email</strong> at the bottom of this page
            </a>
            .
          </p>
          <FormspreeSelect
            isSearchable={options.length > SEARCHABLE_ABOVE}
            options={options}
            onChange={option => {
              setNewEmail(option.value)
            }}
            value={newEmail || profile.email}
          />
          <p className="mt-4 text-sm text-gray-500">
            This'll be the email address you log in with. It'll also be where
            password resets, billing receipts and account notices are sent.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-500">
            This is the email address you log in with. It's also where password
            resets, billing receipts and account notices are sent.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            To change your account email:
          </p>
          <ol className="ml-5 mt-2 list-decimal text-sm text-gray-500">
            <li>
              <a
                className="primaryLink cursor-pointer"
                onClick={() => {
                  closeCurrentModal()
                  showLinkedEmails()
                }}
              >
                Add a new <strong>Linked Email</strong> (bottom of this page)
              </a>
            </li>
            <li>
              Click the <strong>verification link</strong> we email you
            </li>
            <li>Select it as your account email here</li>
          </ol>
        </>
      )}

      {selectable.length > 0 && (
        <Modal.Footer>
          <LoaderButton
            onClick={() => {
              patchAccount({
                payload: {email: newEmail},
                profile: true,
                onSuccess: () => {
                  closeCurrentModal()
                  setNewEmail()
                  // patchAccount only replaces `profile`, but the linked emails
                  // table badges its rows off `account.email`. Refetch so both
                  // agree without a page reload.
                  reloadAccount()
                  ready()
                },
              })
            }}
            disabled={!newEmail || newEmail === profile.email}
          >
            Save
          </LoaderButton>
        </Modal.Footer>
      )}
    </ContextModal>
  )
}

/**
 * scrollIntoView rather than a #hash, because the page scrolls inside
 * DashboardLayout's overflow container, not the window. The class is removed
 * first so the animation replays on a second click.
 */
function showLinkedEmails() {
  const section = document.getElementById('linked-emails')
  section.scrollIntoView({behavior: 'smooth', block: 'start'})
  section.classList.remove('animate-ring-flash')
  requestAnimationFrame(() => {
    section.classList.add('animate-ring-flash')
  })
}
